import { useLayoutEffect, useState } from 'react';

// Measure the rendered content, including media that acquires its size later.
export default function useContentOverflow(viewportRef, contentRef, contentKey, enabled = true) {
  const [overflows, setOverflows] = useState(false);

  useLayoutEffect(() => {
    const viewport = viewportRef.current;
    const content = contentRef.current;
    if (!enabled || !viewport || !content) {
      setOverflows(false);
      return;
    }

    let disposed = false;
    let frame = null;
    const clippedTabStops = new Map();
    const restoreTabStop = element => {
      if (!clippedTabStops.has(element)) return;
      const previous = clippedTabStops.get(element);
      if (previous === null) element.removeAttribute('tabindex');
      else element.setAttribute('tabindex', previous);
      clippedTabStops.delete(element);
    };
    const measure = () => {
      if (disposed) return;
      setOverflows(viewport.scrollHeight > viewport.clientHeight + 1);
      // Focusing a clipped link scrolls even an overflow:hidden viewport.
      // Keep Tab within the visible preview; opening the reader restores all
      // native tab stops, including the source avatar and comment links.
      const bounds = viewport.getBoundingClientRect();
      // Read geometry together before modifying tab stops. Media, fonts and
      // both observers often notify in one frame; they share one measurement.
      const tabStops = Array.from(content.querySelectorAll('a[href], button, input, select, textarea, summary, iframe, [tabindex], [contenteditable="true"]'))
        .map(element => {
          const box = element.getBoundingClientRect();
          const clipped = box.top < bounds.top - 1 || box.bottom > bounds.bottom + 1;
          return { element, clipped };
        });
      tabStops.forEach(({ element, clipped }) => {
        if (!clipped) restoreTabStop(element);
        else if (!clippedTabStops.has(element) && element.tabIndex >= 0) {
          clippedTabStops.set(element, element.getAttribute('tabindex'));
          element.setAttribute('tabindex', '-1');
        }
      });
    };
    const schedule = () => {
      if (disposed || frame !== null) return;
      frame = requestAnimationFrame(() => { frame = null; measure(); });
    };
    // Returning from reading mode must restore the start of the preview.
    viewport.scrollTop = 0;
    measure();
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(schedule);
    observer?.observe(viewport);
    observer?.observe(content);
    // Media can replace a loader with a retry button without changing size.
    const mutations = typeof MutationObserver === 'undefined' ? null : new MutationObserver(schedule);
    mutations?.observe(content, { childList: true, subtree: true });
    content.addEventListener('load', schedule, true);
    content.addEventListener('error', schedule, true);
    content.addEventListener('loadedmetadata', schedule, true);
    window.addEventListener('resize', schedule);
    const fonts = document.fonts;
    fonts?.addEventListener?.('loadingdone', schedule);
    fonts?.ready?.then(schedule);

    return () => {
      disposed = true;
      if (frame !== null) cancelAnimationFrame(frame);
      observer?.disconnect();
      mutations?.disconnect();
      Array.from(clippedTabStops.keys()).forEach(restoreTabStop);
      content.removeEventListener('load', schedule, true);
      content.removeEventListener('error', schedule, true);
      content.removeEventListener('loadedmetadata', schedule, true);
      window.removeEventListener('resize', schedule);
      fonts?.removeEventListener?.('loadingdone', schedule);
    };
  }, [viewportRef, contentRef, contentKey, enabled]);

  return overflows;
}
